import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as CloneIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import roleService from '../../services/roleService';
import { useStore } from '../../context/StoreContext';

const RoleDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    newName: '',
    newDisplayName: ''
  });

  useEffect(() => {
    fetchRole();
  }, [id]);

  const fetchRole = async () => {
    try {
      setLoading(true);
      const data = await roleService.getRoleWithUsers(id);
      setRole(data);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      await roleService.cloneRole(id, cloneForm.newName, cloneForm.newDisplayName);
      toast.success(t('roles.createSuccess'));
      setCloneDialogOpen(false);
      navigate('/roles');
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!role) {
    return <Box sx={{ p: 3 }}>{t('common.notFound')}</Box>;
  }

  const permissionsByResource = role.permissions?.reduce((acc, permission) => {
    const resource = permission.resource || 'Other';
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(permission);
    return acc;
  }, {}) || {};

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/roles')}>
            {t('common.back')}
          </Button>
          <Typography variant="h4">{t('roles.viewRole')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CloneIcon />}
            onClick={() => setCloneDialogOpen(true)}
          >
            {t('roles_ext.cloneRole')}
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/roles/edit/${id}`)}
          >
            {t('roles.editRole')}
          </Button>
        </Box>
      </Box>

      {role.isGlobalRole && (
        <Alert severity="warning" icon={<StoreIcon />} sx={{ mb: 3 }}>
          {t('roles_ext.globalTemplateNotice')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.displayName')}
                  </Typography>
                  <Typography variant="h5">{role.displayName}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('roles_ext.nameIdentifier')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={role.name} size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    bgcolor: role.isGlobalRole ? 'warning.lighter' : 'info.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: role.isGlobalRole ? 'warning.main' : 'info.main'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <StoreIcon color={role.isGlobalRole ? 'warning' : 'info'} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {role.isGlobalRole ? t('common.globalTemplate') : t('roles_ext.storeAssignment')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" color={role.isGlobalRole ? 'warning.dark' : 'info.dark'}>
                      {role.isGlobalRole ? t('roles_ext.availableToAllStores') : role.storeName}
                    </Typography>
                    {role.storeId && (
                      <Button
                        size="small"
                        startIcon={<StoreIcon />}
                        onClick={() => navigate(`/stores/view/${role.storeId}`)}
                        sx={{ mt: 1 }}
                      >
                        {t('roles_ext.viewStore')}
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.description')}
                  </Typography>
                  <Typography variant="body1">
                    {role.description || t('common.noDescription')}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={role.isActive ? t('common.active') : t('common.inactive')}
                      color={role.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('roles.assignedUsers')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={role.userCount} color="primary" size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(role.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.updatedAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(role.updatedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Permissions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('roles.permissions')} ({role.permissions?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {Object.entries(permissionsByResource).map(([resource, permissions]) => (
                  <Box key={resource} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                      {resource}
                    </Typography>
                    <Box>
                      {permissions.map((permission) => (
                        <Chip
                          key={permission.id}
                          label={permission.displayName}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Users with this role */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('roles_ext.usersWithRole', { count: role.userCount })}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {role.users && role.users.length > 0 ? (
                <List>
                  {role.users.map((user) => (
                    <ListItem
                      key={user.id}
                      button
                      onClick={() => navigate(`/users/view/${user.id}`)}
                    >
                      <ListItemText
                        primary={user.name}
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                            <Chip
                              label={user.isActive ? t('common.active') : t('common.inactive')}
                              size="small"
                              color={user.isActive ? 'success' : 'default'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('roles_ext.noUsersWithRole')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)}>
        <DialogTitle>{t('roles_ext.cloneRole')}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('roles_ext.newRoleName')}
                value={cloneForm.newName}
                onChange={(e) => setCloneForm({ ...cloneForm, newName: e.target.value })}
                helperText="e.g., SENIOR_MANAGER"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('roles_ext.newDisplayName')}
                value={cloneForm.newDisplayName}
                onChange={(e) => setCloneForm({ ...cloneForm, newDisplayName: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleClone} variant="contained">
            {t('roles_ext.cloneRole')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleDetails;
